import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { 
  Mic, FileText, Plus, Loader2, AlertTriangle, 
  CheckCircle2, RefreshCw, Database
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EvidencePanel Component
 * 
 * Internal/Operator tool for adding evidence to an engagement.
 * Displays evidence inventory and provides forms to add recordings and transcripts.
 * 
 * Props:
 * - serId: Service Execution Record ID
 * - onEvidenceUpdate: Callback when evidence is updated
 */
const EvidencePanel = ({ serId, onEvidenceUpdate }) => {
  const { token } = useAuth();
  const [evidence, setEvidence] = useState({ recordings: [], transcripts: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Recording form
  const [recordingForm, setRecordingForm] = useState({
    source: "zoom",
    recorded_at: new Date().toISOString().slice(0, 16),
    duration_min: 60,
    consent_flag: false,
    notes: ""
  });
  
  // Transcript form
  const [transcriptForm, setTranscriptForm] = useState({
    source_recording_id: "",
    text: "",
    speaker_tags: false
  });

  const fetchEvidence = async () => {
    if (!serId || !token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/engagements/${serId}/evidence`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.error?.message || 'Failed to fetch evidence');
      }
      
      const data = await response.json();
      setEvidence(data);
      setError(null);
    } catch (err) {
      console.error("Failed to load evidence:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvidence();
  }, [serId, token]);

  const handleAddRecording = async () => {
    if (!recordingForm.consent_flag) {
      toast.error("Recording consent is required");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/evidence/recordings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...recordingForm,
          recorded_at: new Date(recordingForm.recorded_at).toISOString()
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.error?.message || 'Failed to add recording');
      }
      
      const data = await response.json();
      toast.success(`Recording added (ID: ${data.recording_id.slice(0, 8)}...)`);
      
      // Reset form and close modal
      setRecordingForm({
        source: "zoom",
        recorded_at: new Date().toISOString().slice(0, 16),
        duration_min: 60,
        consent_flag: false,
        notes: ""
      });
      setShowRecordingModal(false);
      
      // Refresh evidence
      await fetchEvidence();
      onEvidenceUpdate?.();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddTranscript = async () => {
    if (!transcriptForm.text?.trim()) {
      toast.error("Transcript text is required");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/engagements/${serId}/evidence/transcripts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_recording_id: transcriptForm.source_recording_id || null,
          text: transcriptForm.text,
          speaker_tags: transcriptForm.speaker_tags
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.error?.message || 'Failed to add transcript');
      }
      
      const data = await response.json();
      toast.success(`Transcript added (${data.word_count.toLocaleString()} words)`);
      
      // Reset form and close modal
      setTranscriptForm({
        source_recording_id: "",
        text: "",
        speaker_tags: false
      });
      setShowTranscriptModal(false);
      
      // Refresh evidence
      await fetchEvidence();
      onEvidenceUpdate?.();
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="glass p-6 rounded-xl" data-testid="evidence-panel">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <span className="text-slate-400">Loading evidence...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="glass p-6 rounded-xl" data-testid="evidence-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Database className="w-5 h-5 text-blue-400" />
          <div>
            <h3 className="font-semibold text-white">Evidence Intake (Internal)</h3>
            <p className="text-sm text-slate-400">
              Add recordings and transcripts to this engagement
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchEvidence}
          className="text-slate-400 hover:text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {/* Counts Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-white">{evidence.counts?.recordings || 0}</div>
          <div className="text-xs text-slate-400">Recordings</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-white">{evidence.counts?.transcripts || 0}</div>
          <div className="text-xs text-slate-400">Transcripts</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-white">{evidence.counts?.total_duration_min || 0}</div>
          <div className="text-xs text-slate-400">Total Minutes</div>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-white">
            {(evidence.counts?.total_word_count || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-400">Total Words</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={() => setShowRecordingModal(true)}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
          data-testid="add-recording-btn"
        >
          <Mic className="w-4 h-4 mr-2" />
          Add Recording
        </Button>
        <Button
          onClick={() => setShowTranscriptModal(true)}
          className="flex-1 bg-green-600 hover:bg-green-700"
          data-testid="add-transcript-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          Add Transcript
        </Button>
      </div>

      {/* Existing Evidence List */}
      {(evidence.recordings?.length > 0 || evidence.transcripts?.length > 0) && (
        <div className="mt-6 pt-6 border-t border-slate-700/50">
          <h4 className="text-sm font-medium text-slate-300 mb-3">Evidence Items</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {evidence.recordings?.map((r, idx) => (
              <div key={r.recording_id || idx} className="flex items-center gap-2 text-sm p-2 bg-slate-800/30 rounded">
                <Mic className="w-4 h-4 text-blue-400" />
                <span className="text-slate-300">{r.source}</span>
                <span className="text-slate-500">•</span>
                <span className="text-slate-400">{r.duration_min} min</span>
                {r.consent_flag && <CheckCircle2 className="w-3 h-3 text-green-400 ml-auto" />}
              </div>
            ))}
            {evidence.transcripts?.map((t, idx) => (
              <div key={t.transcript_id || idx} className="flex items-center gap-2 text-sm p-2 bg-slate-800/30 rounded">
                <FileText className="w-4 h-4 text-green-400" />
                <span className="text-slate-300">{t.word_count?.toLocaleString()} words</span>
                {t.speaker_tags && (
                  <>
                    <span className="text-slate-500">•</span>
                    <span className="text-slate-400">Tagged</span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Recording Modal */}
      <Dialog open={showRecordingModal} onOpenChange={setShowRecordingModal}>
        <DialogContent className="bg-navy-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mic className="w-5 h-5 text-blue-400" />
              Add Recording Metadata
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter details about the recorded interview session.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Source</Label>
                <Select 
                  value={recordingForm.source} 
                  onValueChange={(v) => setRecordingForm(f => ({ ...f, source: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="zoom">Zoom</SelectItem>
                    <SelectItem value="in_person">In-Person</SelectItem>
                    <SelectItem value="plaud_note">Plaud Note</SelectItem>
                    <SelectItem value="upload">Upload</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  min="1"
                  value={recordingForm.duration_min}
                  onChange={(e) => setRecordingForm(f => ({ ...f, duration_min: parseInt(e.target.value) || 0 }))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Recorded At</Label>
              <Input
                type="datetime-local"
                value={recordingForm.recorded_at}
                onChange={(e) => setRecordingForm(f => ({ ...f, recorded_at: e.target.value }))}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={recordingForm.notes}
                onChange={(e) => setRecordingForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any notes about this recording..."
                className="mt-1"
              />
            </div>
            
            {/* Consent Checkbox - Required */}
            <div className="p-3 border border-amber-500/30 bg-amber-950/20 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox 
                  checked={recordingForm.consent_flag}
                  onCheckedChange={(checked) => setRecordingForm(f => ({ ...f, consent_flag: checked }))}
                  className="mt-0.5"
                />
                <span className="text-sm text-slate-300">
                  I confirm that recording consent was obtained from all participants.
                  <span className="text-amber-400 ml-1">(Required)</span>
                </span>
              </label>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowRecordingModal(false)}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddRecording}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={submitting || !recordingForm.consent_flag}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Recording
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Transcript Modal */}
      <Dialog open={showTranscriptModal} onOpenChange={setShowTranscriptModal}>
        <DialogContent className="bg-navy-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-400" />
              Add Transcript Text
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Paste the transcript text. Word count will be computed automatically.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {evidence.recordings?.length > 0 && (
              <div>
                <Label>Link to Recording (optional)</Label>
                <Select 
                  value={transcriptForm.source_recording_id} 
                  onValueChange={(v) => setTranscriptForm(f => ({ ...f, source_recording_id: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a recording..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No link</SelectItem>
                    {evidence.recordings.map((r, idx) => (
                      <SelectItem key={r.recording_id || idx} value={r.recording_id}>
                        {r.source} - {r.duration_min} min
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Transcript Text</Label>
              <Textarea
                value={transcriptForm.text}
                onChange={(e) => setTranscriptForm(f => ({ ...f, text: e.target.value }))}
                placeholder="Paste the full transcript text here..."
                className="mt-1 min-h-[200px] font-mono text-sm"
              />
              {transcriptForm.text && (
                <p className="text-xs text-slate-500 mt-1">
                  Word count: {transcriptForm.text.trim().split(/\s+/).filter(w => w).length.toLocaleString()}
                </p>
              )}
            </div>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox 
                checked={transcriptForm.speaker_tags}
                onCheckedChange={(checked) => setTranscriptForm(f => ({ ...f, speaker_tags: checked }))}
              />
              <span className="text-sm text-slate-300">
                Transcript includes speaker identification tags
              </span>
            </label>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowTranscriptModal(false)}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddTranscript}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={submitting || !transcriptForm.text?.trim()}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Transcript
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvidencePanel;
